/**
 * DevQaPanel — 開発(モック)プレビュー用の QA ドロワー
 *
 * 目的: モックプレビューを本番フレーム（1画面フィット）で描画したうえで、
 * 従来ゲームを囲っていたデバッグ群（モード/チケット/デバイスキー/UI05テスト・
 * NODE TIMELINE・変数・強制スキップ・保存状況・RUN STATE）を画面に固定配置した
 * 折りたたみドロワーへ集約する。既定は閉じ、トグルボタンで開閉する。
 *
 * 機能は削除せず移設するのみ。ゲームのレイアウト高さを奪わないよう position:fixed +
 * overlay で描画する。
 */
import { useState } from 'react';
import type { ScenarioNode, VariableDefinition, RunState } from 'shared';
import { ControlPanel } from '../ControlPanel';
import { StepTimeline } from '../StepTimeline';

interface DevQaPanelProps {
  mode: 'mock' | 'live';
  context?: {
    runId?: string;
    deviceKey?: string;
    ticketStatus?: string;
    scenarioTitle?: string;
  };
  node: ScenarioNode | null;
  currentStepIndex: number;
  variableDefinitions: VariableDefinition[];
  variables: Record<string, unknown>;
  runState: RunState;
  /** 強制スキップ（旧「次のStepへ進む」）。本番進行 ▶ とは別の高速ナビ用。 */
  canAdvance: boolean;
  isBusy: boolean;
  isCompleted: boolean;
  onForceAdvance: () => void;
  lastSavedAt?: number | null;
  /** UI05（エラーUI）テスト表示を開く */
  onShowErrorUi05: () => void;
}

function formatSaved(timestamp?: number | null): string {
  if (!timestamp) return '未保存';
  const diff = Date.now() - timestamp;
  if (diff < 5000) return 'たった今';
  if (diff < 60_000) return `${Math.round(diff / 1000)}秒前`;
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}分前`;
  return new Date(timestamp).toLocaleString('ja-JP');
}

export function DevQaPanel({
  mode,
  context,
  node,
  currentStepIndex,
  variableDefinitions,
  variables,
  runState,
  canAdvance,
  isBusy,
  isCompleted,
  onForceAdvance,
  lastSavedAt,
  onShowErrorUi05,
}: DevQaPanelProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="dev-qa">
      <button
        type="button"
        className="dev-qa__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'QAパネルを閉じる' : 'QAパネルを開く'}
      >
        ⚙ QA
      </button>

      {open && (
        <div className="dev-qa__drawer" role="dialog" aria-label="QAパネル">
          <div className="dev-qa__drawer-head">
            <div>
              <p className="eyebrow">QA / Debug</p>
              <strong>{context?.scenarioTitle ?? 'Unknown scenario'}</strong>
              <p className="eyebrow subtle">
                Mode: {mode === 'live' ? 'API Connected' : 'Mock Preview'}
              </p>
            </div>
            <button
              type="button"
              className="dev-qa__close"
              onClick={() => setOpen(false)}
              aria-label="閉じる"
            >
              ✕
            </button>
          </div>

          <div className="dev-qa__drawer-body">
            {/* 強制スキップ（高速ナビ）。本番進行 ▶ とは別物。 */}
            <div className="dev-qa__section">
              <p className="eyebrow">進行（QA用・強制スキップ）</p>
              <button
                className="primary-btn"
                disabled={!canAdvance || isBusy || isCompleted}
                onClick={onForceAdvance}
              >
                次のStepへ進む（スキップ）
              </button>
            </div>

            {/* モード/チケット/デバイスキー/UI05 テスト */}
            <div className="dev-qa__section">
              <ControlPanel mode={mode} context={context} onShowErrorUi05={onShowErrorUi05} />
            </div>

            {/* NODE TIMELINE */}
            <div className="dev-qa__section">
              <StepTimeline node={node} currentIndex={currentStepIndex} />
            </div>

            {/* 変数（インライン表示・本番と被らないよう fixed パネルは使わない） */}
            {variableDefinitions.length > 0 && (
              <div className="dev-qa__section">
                <p className="eyebrow">変数</p>
                <ul className="dev-qa__vars">
                  {variableDefinitions
                    .filter((def) => def.showInDebug !== false)
                    .map((def) => (
                      <li key={def.key}>
                        <span>{def.label || def.key}</span>
                        <code>{String(variables[def.key] ?? def.defaultValue)}</code>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* 保存状況 */}
            <div className="dev-qa__section">
              <p className="eyebrow">保存状況</p>
              <strong>{isBusy ? '保存中…' : formatSaved(lastSavedAt)}</strong>
            </div>

            {/* RUN STATE */}
            <div className="dev-qa__section">
              <p className="eyebrow">Run State</p>
              <code>node: {runState.currentNodeId ?? 'null'}</code>
              <code>step #{runState.currentStepIndex + 1}</code>
              <code>variables: {JSON.stringify(runState.variables)}</code>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DevQaPanel;
